import type { LayerSpecification, SourceSpecification } from 'maplibre-gl';
import type { StylePart } from '@/features/map';
import voyager from './voyager.json';

// CARTO "Voyager" vector basemap (https://carto.com/basemaps), layers vendored from
// https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json so the map style can be composed
// synchronously. Tiles, sprite and glyphs use CARTO's unversioned URLs: the versioned ones in the
// published style change with every release. The key is restricted to the site's origin.

const KEY = import.meta.env.VITE_CARTO_KEY?.trim() ?? '';
const CARTO = 'https://tiles.basemaps.cartocdn.com';

/** CARTO's glyphs, shared by every label on the map (our own layers use fonts it serves). */
export const GLYPHS_URL = `${CARTO}/fonts/{fontstack}/{range}.pbf?key=${KEY}`;

const SOURCE: SourceSpecification = {
  type: 'vector',
  url: `${CARTO}/vector/carto.streets/v2/tiles.json?key=${KEY}`,
};

const PREFIX = 'default-';
const prefixed = (id: string) => PREFIX + id;

const layers = (voyager.layers as LayerSpecification[]).map((l) => ({
  ...l,
  id: prefixed(l.id),
  ...('source' in l && typeof l.source === 'string' && { source: prefixed(l.source) }),
})) as LayerSpecification[];

/** Ids of the default map's layers, to toggle them together. */
export const DEFAULT_MAP_LAYERS = layers.map((l) => l.id);

/** The CARTO vector basemap, ids prefixed so they can't clash with other features. */
export function defaultMapStyle(visible: boolean): StylePart {
  return {
    sprite: `${CARTO}/gl/voyager-gl-style/sprite?key=${KEY}`,
    sources: { [prefixed('protomaps')]: SOURCE },
    layers: layers.map((l) => ({ ...l, layout: { ...l.layout, visibility: visible ? 'visible' : 'none' } })),
  };
}
